<template>
    <VfModal id="m-doMore" close-x close-on-mask-click scrolls>
        <template #header> Do more with ZynoSuite </template>

        <p class="dm-intro">
            ZynoSuite is a whole suite of connected products. Here is everything else it can do — add any module to your account whenever you are
            ready.
        </p>

        <div class="dm-list">
            <!-- Available to add: the whole card links out to the product page. -->
            <a v-for="m in missingModules" :key="m.key" class="dm-row" :href="m.url" target="zynosuite" rel="noopener noreferrer">
                <span class="dm-row__icon"><i :class="m.icon" /></span>
                <span class="dm-row__body">
                    <span class="dm-row__head">
                        <span class="dm-row__name">{{ m.name }}</span>
                        <span class="dm-row__tag">{{ m.tagline }}</span>
                    </span>
                    <span class="dm-row__desc">{{ m.description }}</span>
                </span>
                <i class="fa-solid fa-arrow-up-right dm-row__go" />
            </a>

            <!-- Already on the plan: faded, not actionable. -->
            <div v-for="m in ownedModules" :key="m.key" class="dm-row is-owned">
                <span class="dm-row__icon"><i :class="m.icon" /></span>
                <span class="dm-row__body">
                    <span class="dm-row__head">
                        <span class="dm-row__name">{{ m.name }}</span>
                        <span class="dm-row__tag">{{ m.tagline }}</span>
                    </span>
                    <span class="dm-row__desc">{{ m.description }}</span>
                </span>
                <span class="dm-row__badge"><i class="fa-solid fa-check" /> On your plan</span>
            </div>
        </div>

        <template #footer>
            <span class="dm-foot-hint">Add any module whenever you're ready — start with one, grow into the rest.</span>
            <button type="button" class="zs-btn" @click="callback()">Close</button>
            <a class="zs-btn zs-btn--primary" :href="contactUrl" target="zynosuite" rel="noopener noreferrer" @click="callback()"> Talk to sales </a>
        </template>
    </VfModal>
</template>

<script lang="ts" setup>
import { VfModal } from '@zyno-io/vue-foundation';
import { computed } from 'vue';

import { buildContactSalesUrl, useUpsellState } from '@/modules/core/exports/upsell-modules';

defineProps<{
    callback: () => void;
}>();

const upsell = useUpsellState();

// Lead with the modules the tenant can add; trail with the faded owned ones.
const missingModules = computed(() => upsell.value.missing);
const ownedModules = computed(() => upsell.value.modules.filter(m => m.owned));

const contactUrl = computed(() => buildContactSalesUrl());
</script>

<style>
@reference '#tw.css';

#m-doMore {
    .vf-modal-content {
        width: min(600px, 92vw);
    }

    .dm-intro {
        margin: 0 0 18px;
        font-size: var(--t-sm);
        line-height: 1.5;
        color: var(--ink-2);
    }

    .dm-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .dm-row {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        padding: 14px;
        border: 1px solid var(--line);
        border-radius: 10px;
        background: var(--surface);
        text-decoration: none;
        color: inherit;
        transition:
            border-color 0.12s,
            background 0.12s;
    }

    a.dm-row:hover {
        border-color: var(--accent);
        background: var(--accent-soft);
    }

    .dm-row.is-owned {
        opacity: 0.5;
        background: var(--surface-2);
    }

    .dm-row__icon {
        flex: none;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border-radius: 9px;
        background: var(--accent-tint);
        color: var(--accent);
        font-size: 17px;
    }

    .dm-row.is-owned .dm-row__icon {
        background: var(--line);
        color: var(--ink-3);
    }

    .dm-row__body {
        display: flex;
        flex-direction: column;
        gap: 3px;
        min-width: 0;
    }

    .dm-row__head {
        display: flex;
        align-items: baseline;
        gap: 8px;
        flex-wrap: wrap;
    }

    .dm-row__name {
        font-size: var(--t-base);
        font-weight: 600;
        color: var(--ink);
    }

    .dm-row__tag {
        font-size: var(--t-xs);
        color: var(--ink-3);
    }

    .dm-row__desc {
        font-size: var(--t-sm);
        line-height: 1.45;
        color: var(--ink-2);
    }

    .dm-row__go {
        flex: none;
        align-self: center;
        font-size: 13px;
        color: var(--ink-3);
    }

    a.dm-row:hover .dm-row__go {
        color: var(--accent);
    }

    .dm-row__badge {
        flex: none;
        align-self: center;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: var(--t-xs);
        font-weight: 500;
        color: var(--ink-3);
        white-space: nowrap;
    }

    .dm-foot-hint {
        flex: 1;
        align-self: center;
        font-size: var(--t-xs);
        color: var(--ink-3);
        line-height: 1.4;
    }
}
</style>
